import * as events from '../base/constant';
import { isNullOrUndefined as isNOU } from '@syncfusion/ej2-base';
import { CodeBlockPlugin } from '../../editor-manager/plugin/code-block';
import * as EVENTS from '../../common/constant';
/**
 * Code Block module provides functionality for working with code blocks in the Rich Text Editor
 * Handles code block creation, editing, and keypress interactions
 *
 * @constructor CodeBlock
 */
export class CodeBlock {
    /**
     * Creates an instance of CodeBlock module
     *
     * @param {IRichTextEditor} parent - The parent Rich Text Editor instance
     * @returns {void}
     */
    constructor(parent) {
        this.parent = parent;
        this.isDestroyed = false;
        this.isItemsDisabled = false;
        this.addEventListener();
    }
    /* Registers event handlers for code block operations */
    addEventListener() {
        this.parent.on(events.onCodeBlock, this.onCodeBlock, this);
        this.parent.on(events.codeBlockPaste, this.onPaste, this);
        this.parent.on(events.codeBlockEnter, this.codeBlockEnter, this);
        this.parent.on(events.keyDown, this.onKeyDown, this);
        this.parent.on(events.keyUp, this.onKeyUp, this);
        this.parent.on(events.editAreaClick, this.editAreaClickHandler, this);
        this.parent.on(events.destroy, this.destroy, this);
        this.parent.on(events.bindOnEnd, this.bindOnEnd, this);
    }
    /* Handles code block button or dropdown click events */
    onCodeBlock(args) {
        if (args.member === 'codeBlock' && args.args && args.args.item && args.args.originalEvent.target &&
            args.args.originalEvent.target.parentElement.classList.contains('e-dropdown-btn')) {
            return;
        }
        else if (args.args.originalEvent.target.parentElement.classList.contains('e-split-btn') || args.args.originalEvent.target.classList.contains('e-split-btn')) {
            const codeBlock = this.parent.codeBlockSettings;
            let defaultItem;
            if (codeBlock.languages && codeBlock.languages.length > 0) {
                const filteredItems = !isNOU(codeBlock.defaultLanguage) ?
                    codeBlock.languages.filter((item) => item.language === codeBlock.defaultLanguage) : [];
                defaultItem = filteredItems.length > 0 ? filteredItems[0] : codeBlock.languages[0];
                this.parent.formatter.process(this.parent, args.args, args.args.originalEvent, { language: defaultItem.language, label: defaultItem.label, action: 'createCodeBlock', enterAction: this.parent.enterKey });
            }
        }
    }
    /* Handles paste events within code blocks while pasting remvoe the all the format and past into the code block */
    onPaste(e) {
        const range = this.parent.formatter.editorManager.nodeSelection.getRange(this.parent.inputElement.ownerDocument);
        if (this.codeBlockObj.isValidCodeBlockStructure(range.startContainer) ||
            this.codeBlockObj.isValidCodeBlockStructure(range.endContainer)) {
            const codeBlockTarget = !isNOU(this.codeBlockObj.isValidCodeBlockStructure(range.startContainer)) ?
                this.codeBlockObj.isValidCodeBlockStructure(range.startContainer)
                : this.codeBlockObj.isValidCodeBlockStructure(range.endContainer);
            const currentFormat = this.parent.codeBlockSettings.languages
                .slice()
                .filter((item) => item.label === codeBlockTarget.getAttribute('data-language'));
            let item = e.item;
            if (isNOU(item)) {
                item = {
                    command: 'CodeBlock',
                    subCommand: 'CodeBlock'
                };
            }
            const actionBeginArgs = {
                originalEvent: e.args, item: item
            };
            this.parent.formatter.process(this.parent, actionBeginArgs, e.args, { action: 'codeBlockPaste', currentFormat: { label: currentFormat[0].label, language: currentFormat[0].language } });
        }
    }
    /*
    * Handles Enter key press events within code blocks
    * Maintains proper behavior when users press Enter while editing inside code blocks
    */
    codeBlockEnter(args) {
        if ((args.args) && args.args.which === 13) {
            const range = this.parent.formatter.editorManager.nodeSelection.getRange(this.parent.inputElement.ownerDocument);
            if (this.codeBlockObj.isValidCodeBlockStructure(range.startContainer) ||
                this.codeBlockObj.isValidCodeBlockStructure(range.endContainer)) {
                let item = args.item;
                if (isNOU(item)) {
                    item = {
                        command: 'CodeBlock',
                        subCommand: 'CodeBlock'
                    };
                }
                const actionBeginArgs = {
                    originalEvent: args.args, item: item
                };
                this.parent.formatter.process(this.parent, actionBeginArgs, args.args, { action: 'codeBlockEnter' });
            }
        }
    }
    codeBlockTab(args) {
        const tabAction = (args.args) && args.args.which === 9 && !args.args.shiftKey;
        const shitTabAction = (args.args) && args.args.which === 9 && args.args.shiftKey;
        if ((tabAction) || (shitTabAction)) {
            const range = this.parent.formatter.editorManager.nodeSelection.getRange(this.parent.inputElement.ownerDocument);
            const isCodeBlock = this.codeBlockObj.
                isSelectionWithinCodeBlock(range, range.startContainer, range.endContainer);
            if (isCodeBlock) {
                const item = this.createToolbarItem(args);
                const actionBeginArgs = {
                    originalEvent: args.args,
                    item: item
                };
                const currentAction = tabAction ? 'codeBlockTabAction' : 'codeBlockShiftTabAction';
                this.parent.formatter.process(this.parent, actionBeginArgs, args.args, { action: currentAction });
                args.args.preventDefault();
            }
        }
    }
    /*
     * Handles special key events within code blocks
     * Processes delete/backspace keys and code block shortcuts to maintain proper code block structure
     */
    onKeyDown(e) {
        const keyboardEvent = e.args;
        if (keyboardEvent.which === 9) {
            this.codeBlockTab(e);
            return;
        }
        const isDeleteKey = keyboardEvent.which === 8 || keyboardEvent.which === 46;
        const isCodeBlockShortcut = keyboardEvent.action === 'code-block';
        const range = this.parent.formatter.editorManager.nodeSelection.getRange(this.parent.inputElement.ownerDocument);
        if (keyboardEvent.action === 'enter') {
            const codeBlockPasteAction = (this.codeBlockObj.isValidCodeBlockStructure(range.startContainer) ||
                this.codeBlockObj.isValidCodeBlockStructure(range.endContainer));
            if (!isNOU(codeBlockPasteAction)) {
                this.codeBlockEnter(e);
                return;
            }
        }
        // Only process delete/backspace keys or code block shortcut
        if (!isDeleteKey && !isCodeBlockShortcut) {
            return;
        }
        // Skip if selection contains all content
        if (this.isSelectionAllContent(range)) {
            return;
        }
        const editorManager = this.parent.formatter.editorManager;
        const codeBlockPosition = editorManager.codeBlockObj.getCodeBlockPosition(range);
        // Check if cursor is at the end of a code block (for delete key handling)
        const isCursorAtCodeBlockEnd = !isNOU(codeBlockPosition.nextSiblingCodeBlockElement) &&
            codeBlockPosition.cursorAtLastPosition;
        // Checks if cursor is at the beginning of the current element
        // Used to handle merging with preceding elements when Backspace is pressed
        const firstContentNode = editorManager.nodeSelection.findFirstContentNode(codeBlockPosition.blockNode);
        const isCursorAtBlockStart = firstContentNode &&
            firstContentNode.node === range.startContainer &&
            range.startOffset === 0;
        // Find any previous code block that might be affected by backspace
        const previousSiblingCodeBlock = editorManager.codeBlockObj.findParentOrPreviousSiblingCodeBlock(range);
        /* Determines if the current node should not be a list (UL/OL) to prevent backspace action. */
        const currentNodeShouldNotBeList = !isNOU(previousSiblingCodeBlock) &&
            previousSiblingCodeBlock.currentNode &&
            previousSiblingCodeBlock.currentNode.nodeName !== 'UL' &&
            previousSiblingCodeBlock.currentNode.nodeName !== 'OL';
        const hasPreviousCodeBlock = currentNodeShouldNotBeList && isCursorAtBlockStart;
        // Check if selection is within a code block
        const isStartPointInCodeBlock = !isNOU(this.codeBlockObj.isValidCodeBlockStructure(range.startContainer));
        const isEndPointInCodeBlock = !isNOU(this.codeBlockObj.isValidCodeBlockStructure(range.endContainer));
        const isSelectionInCodeBlock = isStartPointInCodeBlock || isEndPointInCodeBlock;
        const shouldProcessCodeBlock = isSelectionInCodeBlock || isCursorAtCodeBlockEnd ||
            hasPreviousCodeBlock || isCodeBlockShortcut;
        if (shouldProcessCodeBlock) {
            const item = this.createToolbarItem(e);
            const actionBeginArgs = {
                originalEvent: e.args,
                item: item
            };
            // Handle backspace/delete operations
            if (!isCodeBlockShortcut) {
                this.processCodeBlockDeletion(actionBeginArgs, e, isCursorAtCodeBlockEnd, hasPreviousCodeBlock, isSelectionInCodeBlock, range);
            }
            // Handle code block creation shortcut
            else {
                this.createCodeBlockWithDefaultLanguage(actionBeginArgs, e);
            }
        }
    }
    /* Creates a toolbar item model for code block operations
     * If no item exists in the event, creates a default code block item
     */
    createToolbarItem(e) {
        let item = e.item;
        if (isNOU(item)) {
            item = {
                command: 'CodeBlock',
                subCommand: 'CodeBlock'
            };
        }
        return item;
    }
    /*
     * Handles deletion and backspace operations within code blocks
     * Evaluates the cursor position and surrounding elements to properly manage code block structure during deletion and backspace
     */
    processCodeBlockDeletion(actionBeginArgs, e, isAtLastPosition, hasPrevCodeBlock, isInCodeBlock, range) {
        if (!isAtLastPosition && !hasPrevCodeBlock && isInCodeBlock) {
            const codeBlockTarget = !isNOU(this.codeBlockObj.isValidCodeBlockStructure(range.startContainer)) ?
                this.codeBlockObj.isValidCodeBlockStructure(range.startContainer) :
                this.codeBlockObj.isValidCodeBlockStructure(range.endContainer);
            const languageLabel = codeBlockTarget.getAttribute('data-language');
            const languages = this.parent.codeBlockSettings.languages;
            const currentFormat = languages
                .slice()
                .filter(function (item) {
                return item.label === languageLabel;
            });
            if (currentFormat.length > 0) {
                this.parent.formatter.process(this.parent, actionBeginArgs, e.args, {
                    action: 'codeBlockBackSpace',
                    currentFormat: {
                        label: currentFormat[0].label,
                        language: currentFormat[0].language
                    }
                });
            }
        }
        else {
            this.parent.formatter.process(this.parent, actionBeginArgs, e.args, {
                action: 'codeBlockBackSpace'
            });
        }
    }
    /* Creates a code block using the default language configuration
     * Retrieves the default language from settings and applies it to the selected content
     */
    createCodeBlockWithDefaultLanguage(actionBeginArgs, e) {
        const codeBlock = this.parent.codeBlockSettings;
        if (!codeBlock.languages || codeBlock.languages.length === 0) {
            return;
        }
        let defaultItem;
        const hasDefaultLanguage = !isNOU(codeBlock.defaultLanguage);
        if (hasDefaultLanguage) {
            const filteredItems = codeBlock.languages.filter(function (item) {
                return item.language === codeBlock.defaultLanguage;
            });
            defaultItem = filteredItems.length > 0 ? filteredItems[0] : codeBlock.languages[0];
        }
        else {
            defaultItem = codeBlock.languages[0];
        }
        this.parent.formatter.process(this.parent, actionBeginArgs, e.args, {
            language: defaultItem.language,
            label: defaultItem.label,
            action: 'createCodeBlock',
            enterAction: this.parent.enterKey
        });
    }
    /* Determines if the current selection contains all content in the editor
     * Used to prevent certain operations when the entire document is selected
     */
    isSelectionAllContent(range) {
        const div = document.createElement('div');
        div.appendChild(range.cloneContents());
        const selectedHTML = div.innerHTML;
        if (selectedHTML === this.parent.inputElement.innerHTML) {
            return true;
        }
        else {
            return false;
        }
    }
    /* Handles key up events for delete and backspace keys */
    onKeyUp(e) {
        if (e.args.which === 8 || e.args.which === 46) {
            const range = this.parent.formatter.editorManager.nodeSelection.getRange(this.parent.inputElement.ownerDocument);
            const startContainer = range.startContainer.nodeName === '#text' ? range.startContainer.parentElement : range.startContainer;
            const endContainer = range.endContainer.nodeName === '#text' ? range.endContainer.parentElement : range.endContainer;
            if (startContainer.closest('pre[data-language]') && endContainer.closest('pre[data-language]')) {
                let item = e.item;
                if (isNOU(item)) {
                    item = {
                        command: 'CodeBlock',
                        subCommand: 'CodeBlock'
                    };
                }
                const actionBeginArgs = {
                    originalEvent: e.args, item: item
                };
                this.parent.formatter.process(this.parent, actionBeginArgs, e.args, { action: 'codeBlockBackSpace' });
            }
        }
        this.disableToolbarItems();
    }
    /* Handles edit area click events
     * Updates toolbar state based on cursor position
     */
    editAreaClickHandler() {
        this.disableToolbarItems();
    }
    /* Manages toolbar item availability based on code block context
    * When inside a code block, disables most formatting options except those relevant to code blocks
    * Restores all toolbar items when outside of code blocks
    */
    disableToolbarItems() {
        const range = this.parent.formatter.editorManager.nodeSelection.getRange(this.parent.inputElement.ownerDocument);
        const startContainer = range.startContainer.nodeName === '#text' ? range.startContainer.parentElement : range.startContainer;
        const endContainer = range.endContainer.nodeName === '#text' ? range.endContainer.parentElement : range.endContainer;
        const excludeItems = ['undo', 'redo'];
        const disableItemsList = ['codeblock', 'indent', 'outdent', 'sourcecode', 'emojipicker', 'importword', 'exportword', 'exportpdf',
            'lowercase', 'uppercase', 'blockquote', 'numberformatlist', 'bulletformatlist', 'unorderedlist', 'orderedlist', 'fullscreen'];
        if (this.codeBlockObj.isValidCodeBlockStructure(startContainer) ||
            this.codeBlockObj.isValidCodeBlockStructure(endContainer)) {
            this.parent.disableToolbarItem(this.parent.toolbarSettings.items
                .filter((item) => typeof item === 'string' && excludeItems.indexOf(item.toLocaleLowerCase()) === -1));
            this.parent.enableToolbarItem(this.parent.toolbarSettings.items
                .filter((item) => typeof item === 'string' && disableItemsList.indexOf(item.toLocaleLowerCase()) !== -1));
            // Disable the toolbar items when the range is inside a code block
            this.disableTextQuickToolbarItems(true);
            this.isItemsDisabled = true;
        }
        else if (this.isItemsDisabled) {
            const allToolbarItems = this.parent.toolbarSettings.items;
            const enableItems = [...allToolbarItems];
            this.parent.enableToolbarItem((enableItems).filter((item) => typeof item === 'string' && excludeItems.indexOf(item.toLocaleLowerCase()) === -1));
            // Enable the toolbar items when the range is outside of the code block
            this.disableTextQuickToolbarItems(false);
            this.isItemsDisabled = false;
        }
    }
    /*
     * Enables or disables specific items in the text quick toolbar based on whether they're supported in the current context.
     * Only allows specific items like undo, redo, codeblock, etc., while disabling others based on the parameter value.
     */
    disableTextQuickToolbarItems(disable) {
        const allowTextQuickItems = ['undo', 'redo', 'codeblock', 'indent', 'outdent', 'sourcecode', 'emojipicker', 'importword', 'exportword', 'exportpdf',
            'lowercase', 'uppercase', 'blockquote', 'numberformatlist', 'bulletformatlist', 'unorderedlist', 'orderedlist', 'fullscreen'
        ];
        const textQuickToolbar = (this.parent.quickToolbarModule && this.parent.quickToolbarModule.textQTBar &&
            this.parent.quickToolbarModule.textQTBar.quickTBarObj && this.parent.quickToolbarModule.textQTBar.quickTBarObj.toolbarObj) ?
            this.parent.quickToolbarModule.textQTBar.quickTBarObj.toolbarObj : null;
        const toolbarItems = !isNOU(this.parent.quickToolbarSettings.text) ?
            this.parent.quickToolbarSettings.text : [];
        for (let index = 0; index < toolbarItems.length; index++) {
            const item = toolbarItems[index];
            const isAllowed = allowTextQuickItems.indexOf(item.toLocaleLowerCase());
            if (isAllowed === -1 && textQuickToolbar) {
                textQuickToolbar.enableItems(index, !disable);
            }
        }
    }
    /* Removes all event listeners attached by this module
     * Unregisters handlers to prevent memory leaks
     */
    removeEventListener() {
        this.parent.off(events.onCodeBlock, this.onCodeBlock);
        this.parent.off(events.codeBlockPaste, this.onPaste);
        this.parent.off(events.codeBlockEnter, this.codeBlockEnter);
        this.parent.off(events.keyDown, this.onKeyDown);
        this.parent.off(events.keyUp, this.onKeyUp);
        this.parent.off(events.editAreaClick, this.editAreaClickHandler);
        this.parent.off(events.destroy, this.destroy);
        this.parent.off(events.bindOnEnd, this.bindOnEnd);
        this.parent.formatter.editorManager.observer.off(EVENTS.CODEBLOCK_DISABLETOOLBAR, this.editAreaClickHandler);
    }
    /**
     * Cleans up resources and detaches event handlers when the component is destroyed
     *
     * @returns {void}
     */
    destroy() {
        if (this.isDestroyed) {
            return;
        }
        this.isDestroyed = true;
        this.removeEventListener();
    }
    /**
     * For internal use only - Get the module name.
     *
     * @returns {string} - returns the string value
     */
    getModuleName() {
        return 'codeBlock';
    }
    /**
     * Initializes the CodeBlockPlugin object in the editor manager after editor initialization is complete.
     * This method binds the code block  module to the editor's formatter for handling code block related operations.
     *
     * @returns {void} - This method does not return a value
     * @private
     */
    bindOnEnd() {
        if (!this.parent.formatter.editorManager.codeBlockObj) {
            this.parent.formatter.editorManager.codeBlockObj = new CodeBlockPlugin(this.parent.formatter.editorManager);
            this.codeBlockObj = this.parent.formatter.editorManager.codeBlockObj;
            this.parent.formatter.editorManager.observer.on(EVENTS.CODEBLOCK_DISABLETOOLBAR, this.editAreaClickHandler, this);
        }
    }
}
